import { useState } from 'react';

import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';

import { Form } from 'antd';

import { getWarehousesSearch } from '@/api/warehouse';
import { Footer } from '@/components/Footer/Footer';
import { parseQueryParamsForFindWarehouseFilter } from '@/components/PersonalAccountWarehouses/FindWarehouse/SearchForm/utils';
import { SearchLayout } from '@/components/SearchLayout';
import { useUserState } from '@/context/user.context';
import useAsyncEffect from '@/hooks/use-async-effect';
import { TranslationNamespace } from '@/i18n';
import Custom403 from '@/pages/403';
import { CompanyType } from '@/types/common-entities';
import { WarehouseSearchResult } from '@/types/warehouses';

import { AdditionalWarehouseFilters } from './components/AdditionalWarehouseFilters/AdditionalWarehouseFilters';
import { DoYouHaveWarehouseBlock } from './components/DoYouHaveWarehouseBlock/DoYouHaveWarehouseBlock';
import { MainWarehouseFilters } from './components/MainWarehouseFilters/MainWarehouseFilters';
import { WarehousesList } from './components/WarehousesList/WarehousesList';
import { encodedAndFilterValues, fillFormValues, removeEmptyValues } from './utils';

import styles from '@/components/SearchFishToBuy/SearchFishToBuy.module.scss';

const pageSize = 20;

export type MainWarehousesFiltersForm = Omit<
	SearchWarehousesFilters,
	| 'loading_unloading_type'
	| 'has_inspection_room'
	| 'ref_containers'
	| 'certification_importers'
	| 'title'
>;

export type AdditionalWarehousesFiltersForm = Omit<
	SearchWarehousesFilters,
	'location' | 'latitude' | 'longitude' | 'max_distance' | 'temp_regime' | 'free_volume'
>;

export type SearchWarehousesFilters = {
	// Main filters
	/** Адрес */
	location?: string;
	/** Широта */
	latitude?: string;
	/** Долгота */
	longitude?: string;
	/** Радиус поиска */
	max_distance?: number;
	/** Температурный режим */
	temp_regime?: string;
	/** Свободный объем */
	free_volume?: string;

	// Additional filters
	/** Тип погрузки-разгрузки */
	loading_unloading_type?: boolean | number;
	/** Наличие инспекционной комнаты */
	has_inspection_room?: boolean;
	/** Подключение реф. контейнеров */
	ref_containers?: boolean;
	/** Аттестация на требования импортёров */
	certification_importers?: string;
	/** Название склада */
	title?: string;
};

export default function SearchWarehouses() {
	const { t } = useTranslation(TranslationNamespace.WarehouseSearch);
	const router = useRouter();
	const [mainWarehouseFiltersForm] = Form.useForm<MainWarehousesFiltersForm>();
	const [additionalWarehouseFiltersForm] = Form.useForm<AdditionalWarehousesFiltersForm>();

	const [warehouses, setWarehouses] = useState<WarehouseSearchResult['results']>([]);
	const [isWarehousesLoading, setIsWarehousesLoading] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [isFiltersReady, setIsFiltersReady] = useState(false);
	const [isShownNearWarehouses, setIsShownNearWarehouses] = useState(false);
	const { user } = useUserState();

	useAsyncEffect(async () => {
		const filtersFromQuery: SearchWarehousesFilters = parseQueryParamsForFindWarehouseFilter(
			router.query,
		);

		fillFormValues({
			queryParams: filtersFromQuery,
			mainFormValue: mainWarehouseFiltersForm,
			additionalFormValue: additionalWarehouseFiltersForm,
		});
		setIsFiltersReady(true);

		try {
			setIsWarehousesLoading(true);
			const response = await getWarehousesSearch({
				filters: { ...filtersFromQuery, page: 1, page_size: pageSize },
			});

			setIsShownNearWarehouses(response?.fallback);
			setCurrentPage(1);
			setWarehouses(response.results);
			setTotalCount(response.count);
		} catch (error) {
			console.log(error);
		} finally {
			setIsWarehousesLoading(false);
		}
	}, []);

	if (user && user.companyType === CompanyType.Warehouse) {
		return <Custom403 />;
	}

	// Применяет фильтры
	const applyFilters = async (filtersValue: SearchWarehousesFilters) => {
		try {
			setIsWarehousesLoading(true);
			const response = await getWarehousesSearch({
				filters: { ...filtersValue, page: 1, page_size: pageSize },
			});

			setIsShownNearWarehouses(response?.fallback);
			setCurrentPage(1);
			setWarehouses(response.results);
			setTotalCount(response.count);
		} catch (error) {
			console.log(error);
		} finally {
			setIsWarehousesLoading(false);
		}
	};

	const getFilters = (): SearchWarehousesFilters =>
		removeEmptyValues({
			...mainWarehouseFiltersForm.getFieldsValue(),
			...additionalWarehouseFiltersForm.getFieldsValue(),
		});

	const loadMoreHandler = async () => {
		const filters = getFilters();

		try {
			setIsLoadingMore(true);
			const response = await getWarehousesSearch({
				filters: { ...filters, page: currentPage + 1, page_size: pageSize },
			});
			setCurrentPage((currentPage) => currentPage + 1);
			setWarehouses([...warehouses, ...response.results]);
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoadingMore(false);
		}
	};

	const handleSubmit = async () => {
		const filters = getFilters();

		router.replace(
			{
				pathname: router.pathname,
				query: {
					...encodedAndFilterValues(filters),
					...(router.query.warehouseId ? { warehouseId: router.query.warehouseId } : {}),
				},
			},
			undefined,
			{ shallow: true },
		);

		await applyFilters(filters);
	};

	const mainFilters = (
		<MainWarehouseFilters
			mainFilters={mainWarehouseFiltersForm}
			isDisabledSubmitBtn={isWarehousesLoading}
			onSubmit={handleSubmit}
		/>
	);

	return (
		<>
			<SearchLayout
				title={<div className={styles.title}>{t('title')}</div>}
				header={mainFilters}
				sidebar={
					<div>
						<AdditionalWarehouseFilters
							additionalFilters={additionalWarehouseFiltersForm}
							submitBtnDisabled={isWarehousesLoading}
							isLoading={!isFiltersReady}
							onSubmit={handleSubmit}
						/>
						<DoYouHaveWarehouseBlock />
					</div>
				}
				body={
					<WarehousesList
						isLoading={isWarehousesLoading}
						isLoadingMore={isLoadingMore}
						hasMore={warehouses?.length < totalCount}
						warehouses={warehouses}
						loadMoreDisabled={isWarehousesLoading || isLoadingMore}
						count={totalCount}
						isShownNearWarehouses={isShownNearWarehouses}
						onLoadMore={loadMoreHandler}
						getFilters={getFilters}
					/>
				}
			/>
			<Footer />
		</>
	);
}
